import React, { useState, useEffect } from 'react';
import { FiStar, FiX, FiThumbsUp, FiThumbsDown, FiImage } from 'react-icons/fi';
import apiClient from '../utils/apiClient';
import { useShopping } from '../context/ShoppingContext';
import { useToast } from '../context/ToastContext';
import '../styles/Reviews.css';
import '../styles/ReviewsActionButtons.css';

const Reviews = ({ productId }) => {
  const { user } = useShopping();
  const toast = useToast();
  const [reviews, setReviews] = useState([]);
  const [userReview, setUserReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // New state for verification and images
  const [isVerified, setIsVerified] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]); // For editing

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  // Re-fetch reviews when user logs in/out
  useEffect(() => {
    if (user) {
      fetchReviews();
    } else {
      setIsVerified(false);
      setUserReview(null);
    }
  }, [user]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/products/${productId}/reviews`);
      setReviews(response.data || []);

      if (user) {
        const statusResponse = await apiClient.get(
          `/products/${productId}/reviews/user/${user.uid}`
        );

        const { review, isVerified: verifiedStatus } = statusResponse.data;

        setIsVerified(verifiedStatus);

        if (review && review.id) {
          setUserReview(review);
        } else {
          setUserReview(null);
        }
      }

      // Calculate average rating
      if (response.data && response.data.length > 0) {
        const avg = (
          response.data.reduce((sum, r) => sum + r.rating, 0) / response.data.length
        ).toFixed(1);
        setAverageRating(avg);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = files.length + existingImages.length;

    if (totalImages > 4) {
      toast.error('You can upload a maximum of 4 images');
      return;
    }

    // Validate file types
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      toast.error('Only image files are allowed');
      return;
    }

    setSelectedFiles(files);
  };

  const uploadImages = async () => {
    if (selectedFiles.length === 0) return [];

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('images', file);
    });

    try {
      const response = await apiClient.post('/uploads/review-images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      return response.data.urls;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw new Error('Failed to upload images');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to add a review');
      return;
    }

    if (!rating || rating === 0) {
      toast.error('Please select a rating (1-5 stars)');
      return;
    }

    setSubmitting(true);
    try {
      let imageUrls = [];
      if (selectedFiles.length > 0) {
        imageUrls = await uploadImages();
      }

      await apiClient.post(`/products/${productId}/reviews`, {
        rating: parseInt(rating),
        title,
        comment,
        images: imageUrls
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      toast.success('Review submitted successfully!');
      resetForm();
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!userReview) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await apiClient.delete(`/products/reviews/${userReview.id}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      toast.success('Review deleted successfully');
      setUserReview(null);
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const handleEditReview = () => {
    if (userReview) {
      setRating(userReview.rating);
      setTitle(userReview.title);
      setComment(userReview.comment);
      setExistingImages(userReview.images || []);
      setSelectedFiles([]);
      setIsEditing(true);
      setShowForm(true);
    }
  };

  const handleUpdateReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to update a review');
      return;
    }

    if (!rating || rating === 0) {
      toast.error('Please select a rating (1-5 stars)');
      return;
    }

    setSubmitting(true);
    try {
      let newImageUrls = [];
      if (selectedFiles.length > 0) {
        newImageUrls = await uploadImages();
      }

      // Combine existing images (that verify logic of not deleting them) + new uploaded ones
      const finalImages = [...existingImages, ...newImageUrls];

      await apiClient.put(`/products/reviews/${userReview.id}`, {
        rating: parseInt(rating),
        title,
        comment,
        images: finalImages
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      toast.success('Review updated successfully!');
      resetForm();
      fetchReviews();
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to update review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setComment('');
    setRating(5);
    setSelectedFiles([]);
    setExistingImages([]);
    setShowForm(false);
    setIsEditing(false);
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const removeExistingImage = (index) => {
    const newImages = [...existingImages];
    newImages.splice(index, 1);
    setExistingImages(newImages);
  };

  const handleVote = async (reviewId, voteType) => {
    if (!user) {
      toast.error('Please login to vote');
      return;
    }

    try {
      const response = await apiClient.post(`/products/reviews/${reviewId}/vote`, {
        voteType
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      // Update reviews state
      setReviews(prevReviews =>
        prevReviews.map(r =>
          r.id === reviewId ? { ...r, helpful: response.data.helpful, unhelpful: response.data.unhelpful } : r
        )
      );
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to register vote');
    }
  };

  if (loading) {
    return <div className="reviews-section">Loading reviews...</div>;
  }

  return (
    <>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Review?</h3>
            <p>Are you sure you want to delete your review? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="delete-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="reviews-section">
        <div className="reviews-header">
          <h2>Customer Reviews</h2>
          <div className="rating-summary">
            <div className="average-rating">
              <span className="rating-number">{averageRating}</span>
              <span className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <FiStar
                    key={i}
                    className={i < Math.round(averageRating) ? 'filled' : ''}
                    size={16}
                  />
                ))}
              </span>
              <span className="total-reviews">({reviews.length} reviews)</span>
            </div>
          </div>
        </div>

        {/* User's Review */}
        {userReview && (
          <div className="user-review-card">
            <div className="review-header">
              <div className="review-rating">
                {[...Array(5)].map((_, i) => (
                  <FiStar
                    key={i}
                    className={i < userReview.rating ? 'filled' : ''}
                    size={14}
                  />
                ))}
              </div>
              {userReview.verified && <span className="review-verified">✓ Verified Purchase</span>}
              <div className="review-actions">
                <button
                  className="edit-review-btn"
                  onClick={handleEditReview}
                  title="Edit review"
                >
                  Edit
                </button>
                <button
                  className="delete-review-btn"
                  onClick={handleDeleteReview}
                  title="Delete review"
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>
            <h4 className="review-title">Your Review: {userReview.title}</h4>
            <p className="review-comment">{userReview.comment}</p>

            {/* Display Images in User Review */}
            {userReview.images && userReview.images.length > 0 && (
              <div className="review-images">
                {userReview.images.map((img, index) => (
                  <div key={index} className="review-image-thumbnail">
                    <img src={img} alt={`Review ${index + 1}`} onClick={() => window.open(img, '_blank')} />
                  </div>
                ))}
              </div>
            )}

            <span className="review-date">
              {new Date(userReview.createdAt).toLocaleDateString()}
            </span>
          </div>
        )}

        {/* Review Form or Login Prompt */}
        {user && (!userReview || (userReview && isEditing)) && (
          <div className="review-form-container">
            {!showForm ? (
              <button
                className="add-review-btn"
                onClick={() => setShowForm(true)}
              >
                Write a Review
              </button>
            ) : (
              <form onSubmit={isEditing ? handleUpdateReview : handleSubmitReview} className="review-form">
                <h3>{isEditing ? 'Edit your review' : 'Share your feedback'}</h3>

                <div className="form-group">
                  <label>Rating *</label>
                  <div
                    className="rating-selector"
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        className="star-btn"
                        onClick={() => setRating(value)}
                        onMouseEnter={() => setHoverRating(value)}
                      >
                        <FiStar
                          size={24}
                          style={{
                            fill: value <= (hoverRating || rating) ? '#FFA41C' : 'none',
                            stroke: value <= (hoverRating || rating) ? '#FFA41C' : '#ccc',
                            transition: 'all 0.2s'
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="title">Review Title</label>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Great product, highly recommended!"
                    maxLength="100"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="comment">Your Review</label>
                  <textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Share your experience with this product..."
                    rows="4"
                    maxLength="500"
                  />
                  <span className="char-count">{comment.length}/500</span>
                </div>

                {/* Verified Purchase Image Upload */}
                {isVerified && (
                  <div className="form-group">
                    <label>Add Photos (Max 4)</label>
                    <div className="image-upload-container">
                      {(existingImages.length + selectedFiles.length) < 4 && (
                        <label className="file-upload-btn">
                          <FiImage size={20} />
                          <span>Add Photos</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageChange}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}

                      <div className="selected-images-preview">
                        {existingImages.map((img, index) => (
                          <div key={`existing-${index}`} className="preview-item">
                            <img src={img} alt={`Existing ${index}`} />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => removeExistingImage(index)}
                            >
                              <FiX size={12} />
                            </button>
                          </div>
                        ))}
                        {selectedFiles.map((file, index) => (
                          <div key={`new-${index}`} className="preview-item">
                            <img src={URL.createObjectURL(file)} alt={`New ${index}`} />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => removeSelectedFile(index)}
                            >
                              <FiX size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <small className="helper-text">Only verified purchasers can upload images.</small>
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? (isEditing ? 'Updating...' : 'Submitting...') : (isEditing ? 'Update Review' : 'Submit Review')}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {!user && (
          <div className="login-prompt">
            <p>Please Log In to write a review</p>
            <a href="/account" className="login-btn">Log In</a>
          </div>
        )}

        {/* Reviews List */}
        <div className="reviews-list">
          {reviews.length === 0 ? (
            <p className="no-reviews">No reviews yet. Be the first to review!</p>
          ) : (
            reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-user-info">
                  <div className="user-profile-icon">
                    <img
                      src="https://images-eu.ssl-images-amazon.com/images/S/amazon-avatars-global/default._CR0,0,1024,1024_SX48_.png"
                      alt="User"
                      style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                    />
                  </div>
                  <span className="review-user-name">{review.userName || 'Amazon Customer'}</span>
                </div>

                <div className="review-rating-row">
                  <div className="review-rating">
                    {[...Array(5)].map((_, i) => (
                      <FiStar
                        key={i}
                        className={i < review.rating ? 'filled' : ''}
                        size={14}
                      />
                    ))}
                  </div>
                  <span className="review-title-text">{review.title}</span>
                </div>

                <div className="review-meta">
                  Reviewed in India on {new Date(review.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>

                {review.verified && (
                  <div className="review-variant-info">
                    <span className="review-verified">Verified Purchase</span>
                  </div>
                )}

                <p className="review-comment">{review.comment}</p>

                {/* Display Review Images */}
                {review.images && review.images.length > 0 && (
                  <div className="review-images">
                    {review.images.map((img, index) => (
                      <div key={index} className="review-image-thumbnail">
                        <img src={img} alt={`Review by ${review.userName}`} onClick={() => window.open(img, '_blank')} />
                      </div>
                    ))}
                  </div>
                )}

                <div className="review-helpful-actions">
                  <div className="helpful-pill">
                    <button
                      className="pill-btn like-btn"
                      title="Mark as helpful"
                      onClick={() => handleVote(review.id, 'helpful')}
                    >
                      <FiThumbsUp size={18} />
                      <span className="helpful-count-text">
                        {review.helpful >= 1000 ? (review.helpful / 1000).toFixed(1) + 'K' : review.helpful}
                      </span>
                    </button>
                    <div className="pill-separator"></div>
                    <button
                      className="pill-btn dislike-btn"
                      title="Mark as unhelpful"
                      onClick={() => handleVote(review.id, 'unhelpful')}
                    >
                      <FiThumbsDown size={18} />
                      <span className="helpful-count-text">
                        {review.unhelpful >= 1000 ? (review.unhelpful / 1000).toFixed(1) + 'K' : (review.unhelpful || 0)}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Reviews;
